import {QueryCacheKeys} from '../../api/cache';
import {getTags} from '../../api/tags';
import {addTagsToTransactions, removeTagsFromTransactions} from '../../api/transactions';
import {stopPropagation} from '../../util/function';
import TagPicker from '../tags/TagPicker';
import {Icon, Popout, Tooltip} from '@sproutsocial/racine';
import {MOTION_DURATION_MEDIUM} from '@sproutsocial/seeds-motion/dist/seeds-motion-unitless';
import {useQuery} from '@tanstack/react-query';
import {SyntheticEvent, useCallback, useMemo, useState} from 'react';
import {keyBy} from 'shared/src/map';
import type {Transaction} from 'shared/src/models';

interface TransactionTagPickerProps {
	transactions: Transaction[];
	/**
	 * True for read-only mode
	 */
	disabled?: boolean;
	onChange?: () => void;
}

const TransactionTagPicker = ({transactions, disabled, onChange}: TransactionTagPickerProps) => {
	const [isOpen, setIsOpen] = useState(false);
	const onClick = useCallback(
		(event: SyntheticEvent) => {
			event.stopPropagation();

			setIsOpen(disabled ? false : !isOpen);
		},
		[isOpen, disabled],
	);

	const {data: tags} = useQuery({
		queryKey: QueryCacheKeys.tags(),
		queryFn: getTags,
		select: (tags) => keyBy(tags, (tag) => tag.id),
	});

	const selectedTagIds =
		transactions.length === 0
			? new Set<string>()
			: transactions
					.map((transaction) => new Set<string>(transaction.tags))
					.reduce((a, b) => a.intersection(b));

	const selectedTagNames = useMemo<string>(() => {
		return Array.from(
			new Set(
				transactions.flatMap((transaction) =>
					transaction.tags?.map((tagId) => tags?.get(tagId)?.name),
				),
			),
		).join(', ');
	}, [transactions, tags]);

	const indeterminateTagIds =
		transactions.length <= 1
			? new Set<string>()
			: new Set(transactions.flatMap((transaction) => transaction.tags)).difference(selectedTagIds);

	const onTagSelected = useCallback(
		async (tagId: string) => {
			await addTagsToTransactions(
				transactions.map((transaction) => transaction.id),
				[tagId],
			);

			onChange?.();
		},
		[transactions, onChange],
	);

	const onTagDeselected = useCallback(
		async (tagId: string) => {
			await removeTagsFromTransactions(
				transactions.map((transaction) => transaction.id),
				[tagId],
			);

			onChange?.();
		},
		[transactions, onChange],
	);

	return (
		<Popout
			isOpen={isOpen}
			setIsOpen={setIsOpen}
			content={
				<Popout.Content onClick={stopPropagation}>
					<TagPicker
						selectedTagIds={selectedTagIds}
						indeterminateTagIds={indeterminateTagIds}
						onTagSelected={onTagSelected}
						onTagDeselected={onTagDeselected}
					/>
				</Popout.Content>
			}
		>
			<Tooltip
				content={transactions.length === 1 ? selectedTagNames : undefined}
				enterDelay={MOTION_DURATION_MEDIUM * 1000}
			>
				<Icon
					cursor={disabled ? undefined : 'pointer'}
					name={
						selectedTagIds.size > 0 || indeterminateTagIds.size > 0 ? 'tag-solid' : 'tag-outline'
					}
					onClick={onClick}
				/>
			</Tooltip>
		</Popout>
	);
};

export default TransactionTagPicker;
